import * as React from 'react';
import { motion } from 'framer-motion';

import {
  CheckMarkIcon,
  CopyIcon,
  DuplicateIcon
} from 'shared/newComponents/icons';
import { IconSize, IconStyle } from 'shared/entities/components/Icon';
import { ComponentType } from 'shared/entities/components/Component';
import { useModalState } from 'shared/entities/common/hooks';
import { copyToClipboard } from 'shared/entities/common/utils';

import './CopyComponent.modules.scss';

type CopySignProps = {
  value: string;
  className?: string;
  iconVariant?: 'copy' | 'duplicate';
  iconSize?: IconSize;
  iconStyle?: IconStyle;
  onCopy?: (value: string) => void;
};

const CopyComponent: React.FC<CopySignProps> = ({
  value,
  className,
  iconVariant = 'copy',
  iconSize = IconSize.XM,
  iconStyle,
  onCopy
}: CopySignProps) => {
  const copyingTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const {
    opened: copied,
    open: copy,
    close: cancelCopy
  } = useModalState(false);

  React.useEffect(() => {
    return () => {
      if (copyingTimeout.current) {
        clearTimeout(copyingTimeout.current);
      }
    };
  }, []);

  const handleCopy = React.useCallback(async () => {
    const response = await copyToClipboard(value);

    if (response.isError) {
      return;
    }
    copy();
    onCopy?.(value);

    copyingTimeout.current = setTimeout(cancelCopy, 2000);
  }, [value, onCopy]);

  const Icon = iconVariant === 'copy' ? CopyIcon : DuplicateIcon;

  return (
    <div styleName="copy-sign" className={className}>
      {copied && (
        <motion.div
          key={1}
          initial={{ opacity: 0, display: 'flex' }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.7
          }}
        >
          <CheckMarkIcon iconSize={iconSize} />
        </motion.div>
      )}
      {!copied && (
        <motion.div
          key={2}
          initial={{ opacity: 0, display: 'flex' }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.7
          }}
        >
          <Icon
            action={{
              type: ComponentType.svg,
              onClick: handleCopy
            }}
            iconSize={iconSize}
            iconStyle={iconStyle}
          />
        </motion.div>
      )}
    </div>
  );
};

export default React.memo(CopyComponent);
